import axios from 'axios';
import { Hospital, Doctor, Patient, Department, Appointment, ApiResponse, PaginatedResponse } from '../types';

export const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';
// export const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://mihealth.phsghana.com/api';
console.log('API_BASE_URL', API_BASE_URL)
// Create axios instance
const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add auth token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle auth errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Auth API
export const authAPI = {
  login: async (email: string, password: string) => {
    const response = await api.post('/auth/login', { email, password });
    return response.data;
  },
  
  register: async (userData: any) => {
    const response = await api.post('/auth/register', userData);
    return response.data;
  },
  
  getProfile: async () => {
    const response = await api.get('/auth/profile');
    return response.data;
  },
};

// Hospital API
export const hospitalAPI = {
  getAll: async (page = 1, limit = 10, search = ''): Promise<PaginatedResponse<Hospital>> => {
    const response = await api.get(`/hospitals?page=${page}&limit=${limit}&search=${search}`);
    return response.data;
  },
  
  getById: async (id: string): Promise<Hospital> => {
    const response = await api.get(`/hospitals/${id}`);
    return response.data;
  },
  
  create: async (hospitalData: any): Promise<ApiResponse<Hospital>> => {
    const response = await api.post('/hospitals', hospitalData);
    return response.data;
  },
  
  update: async (id: string, hospitalData: any): Promise<ApiResponse<Hospital>> => {
    const response = await api.put(`/hospitals/${id}`, hospitalData);
    return response.data;
  },
  
  delete: async (id: string): Promise<ApiResponse<null>> => {
    const response = await api.delete(`/hospitals/${id}`);
    return response.data;
  },
  
  uploadImages: async (id: string, files: FileList): Promise<ApiResponse<string[]>> => {
    const formData = new FormData();
    Array.from(files).forEach((file) => {
      formData.append('images', file);
    });
    
    const response = await api.post(`/hospitals/${id}/images`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  },

  getNearby: async (lat: number, lng: number, maxDistance = 10000): Promise<ApiResponse<Hospital[]>> => {
    const response = await api.get('/hospitals/nearby', {
      params: { latitude: lat, longitude: lng, maxDistance }
    });
    return response.data;
  },

  geocodeAddress: async (address: string): Promise<{ lat: number; lng: number }> => {
    const response = await fetch(
      `https://maps.googleapis.com/maps/api/geocode/json?address=${encodeURIComponent(address)}&key=${process.env.REACT_APP_GOOGLE_MAPS_API_KEY}`
    );
    const data = await response.json();
    if (data.results && data.results.length > 0) {
      const { lat, lng } = data.results[0].geometry.location;
      return { lat, lng };
    }
    throw new Error('Could not geocode address');
  },

  // Bed Management APIs
  getBeds: async (hospitalId: string): Promise<ApiResponse<any>> => {
    const response = await api.get(`/hospitals/${hospitalId}/beds`);
    return response.data;
  },

  updateBedStatus: async (hospitalId: string, bedId: string, status: 'available' | 'booked', patientName?: string): Promise<ApiResponse<any>> => {
    const response = await api.patch(`/hospitals/${hospitalId}/beds/${bedId}`, {
      status,
      patientName,
      admissionDate: status === 'booked' ? new Date().toISOString() : undefined
    });
    return response.data;
  }
};

// Doctor API
export const doctorAPI = {
  getAll: async (page = 1, limit = 10, hospitalId?: string): Promise<PaginatedResponse<Doctor>> => {
    const params = new URLSearchParams({
      page: page.toString(),
      limit: limit.toString(),
    });
    if (hospitalId) params.append('hospitalId', hospitalId);
    
    const response = await api.get(`/doctors?${params}`);
    return response.data;
  },
  
  getById: async (id: string): Promise<ApiResponse<Doctor>> => {
    const response = await api.get(`/doctors/${id}`);
    return response.data;
  },
  
  create: async (doctorData: any): Promise<ApiResponse<Doctor>> => {
    const response = await api.post('/doctors', doctorData);
    return response.data;
  },
  
  update: async (id: string, doctorData: any): Promise<ApiResponse<Doctor>> => {
    const response = await api.put(`/doctors/${id}`, doctorData);
    return response.data;
  },
  
  delete: async (id: string): Promise<ApiResponse<null>> => {
    const response = await api.delete(`/doctors/${id}`);
    return response.data;
  },
};

// Patient API
export const patientAPI = {
  getAll: async (page = 1, limit = 10, search = ''): Promise<PaginatedResponse<Patient>> => {
    const response = await api.get(`/patients?page=${page}&limit=${limit}&search=${search}`);
    return response.data;
  },
  
  getById: async (id: string): Promise<ApiResponse<Patient>> => {
    const response = await api.get(`/patients/${id}`);
    return response.data;
  },
  
  create: async (patientData: any): Promise<ApiResponse<Patient>> => {
    const response = await api.post('/patients', patientData);
    return response.data;
  },
  
  update: async (id: string, patientData: any): Promise<ApiResponse<Patient>> => {
    const response = await api.put(`/patients/${id}`, patientData);
    return response.data;
  },
  
  delete: async (id: string): Promise<ApiResponse<null>> => {
    const response = await api.delete(`/patients/${id}`);
    return response.data;
  },
};

// Department API
export const departmentAPI = {
  getAll: async (hospitalId?: string): Promise<ApiResponse<Department[]>> => {
    const params = hospitalId ? `?hospitalId=${hospitalId}` : '';
    const response = await api.get(`/departments${params}`);
    return response.data;
  },
  
  getById: async (id: string): Promise<ApiResponse<Department>> => {
    const response = await api.get(`/departments/${id}`);
    return response.data;
  },
  
  create: async (departmentData: any): Promise<ApiResponse<Department>> => {
    const response = await api.post('/departments', departmentData);
    return response.data;
  },
  
  update: async (id: string, departmentData: any): Promise<ApiResponse<Department>> => {
    const response = await api.put(`/departments/${id}`, departmentData);
    return response.data;
  },
  
  delete: async (id: string): Promise<ApiResponse<null>> => {
    const response = await api.delete(`/departments/${id}`);
    return response.data;
  },
};

// Appointment API
export const appointmentAPI = {
  getAll: async (page = 1, limit = 10, filters?: any): Promise<PaginatedResponse<Appointment>> => {
    const params = new URLSearchParams({
      page: page.toString(),
      limit: limit.toString(),
    });
    
    if (filters) {
      Object.keys(filters).forEach(key => {
        if (filters[key]) params.append(key, filters[key]);
      });
    }
    
    const response = await api.get(`/appointments?${params}`);
    return response.data;
  },
  
  getById: async (id: string): Promise<ApiResponse<Appointment>> => {
    const response = await api.get(`/appointments/${id}`);
    return response.data;
  },
  
  create: async (appointmentData: any): Promise<ApiResponse<Appointment>> => {
    const response = await api.post('/appointments', appointmentData);
    return response.data;
  },
  
  update: async (id: string, appointmentData: any): Promise<ApiResponse<Appointment>> => {
    const response = await api.put(`/appointments/${id}`, appointmentData);
    return response.data;
  },
  
  updateStatus: async (id: string, status: string): Promise<ApiResponse<Appointment>> => {
    const response = await api.patch(`/appointments/${id}/status`, { status });
    return response.data;
  },
  
  delete: async (id: string): Promise<ApiResponse<null>> => {
    const response = await api.delete(`/appointments/${id}`);
    return response.data;
  },
};

// Analytics API
export const analyticsAPI = {
  getDashboardStats: async () => {
    const response = await api.get('/analytics/dashboard');
    return response.data;
  },
  
  getHospitalStats: async (hospitalId: string) => {
    const response = await api.get(`/analytics/hospital/${hospitalId}`);
    return response.data;
  },
  
  getBedOccupancy: async (hospitalId?: string) => {
    const params = hospitalId ? `?hospitalId=${hospitalId}` : '';
    const response = await api.get(`/analytics/bed-occupancy${params}`);
    return response.data;
  },
};

export default api;
